/**
 * FilterBar — Barra compacta de filtros inline (1 linha) com borda.
 * Estilo: barra tipo Windows Explorer — busca + chips de filtros ativos.
 */
import { useState, useRef, useEffect, FC, ReactNode } from 'react';
import { FaTimes, FaChevronDown } from 'react-icons/fa';
import { FiFilter, FiSearch } from 'react-icons/fi';

// ─── Types ──────────────────────────────────────────────────────────────────────

export type FieldType = 'text' | 'enum' | 'date';

export interface FilterOption {
    value: string;
    label: string;
}

export interface FilterSchema {
    key: string;
    label: string;
    type: FieldType;
    options?: FilterOption[];
}

export interface AppliedFilter {
    key: string;
    value: string;
}

// ─── Props ──────────────────────────────────────────────────────────────────────

export interface CheckItem {
    key: string;
    label: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
}

interface Props {
    schema: FilterSchema[];
    onSearch: (filters: AppliedFilter[]) => void;
    searchValue?: string;
    onSearchChange?: (value: string) => void;
    onSearchSubmit?: (value?: string) => void;
    searchPlaceholder?: string;
    loading?: boolean;
    className?: string;
    /** Chave para persistir filtros no localStorage. Se não informado, não persiste. */
    storageKey?: string;
    /** Estado inicial dos chips (ex.: quando os filtros vivem na URL via react-router). */
    initialFilters?: AppliedFilter[];
    /** Itens de checagem exibidos no menu dropdown do filtro */
    checkItems?: CheckItem[];
    /** Classe utilitária para a largura do input de busca (default: w-48). */
    searchInputClassName?: string;
    /** Conteúdo sempre visível no início da barra (ex.: botões de canal com ícones). */
    leadingSlot?: ReactNode;
}

// ─── Component ──────────────────────────────────────────────────────────────────

const FilterBar: FC<Props> = ({
    schema,
    onSearch,
    searchValue = '',
    onSearchChange,
    onSearchSubmit,
    searchPlaceholder = 'Buscar...',
    loading = false,
    className,
    storageKey,
    initialFilters,
    checkItems,
    searchInputClassName = 'w-48',
    leadingSlot,
}) => {
    // Restore from localStorage if storageKey is provided; otherwise seed from initialFilters
    // (used when the filters live in the URL via react-router).
    const loadPersistedState = () => {
        if (storageKey) {
            try {
                const raw = localStorage.getItem(storageKey);
                if (raw) {
                    const parsed = JSON.parse(raw);
                    return { keys: parsed.activeKeys || [], vals: parsed.values || {} };
                }
            } catch { /* ignore corrupted data */ }
        }
        if (initialFilters && initialFilters.length) {
            return {
                keys: initialFilters.map((f) => f.key),
                vals: Object.fromEntries(initialFilters.map((f) => [f.key, f.value])),
            };
        }
        return { keys: [], vals: {} };
    };

    const persisted = useRef(loadPersistedState());
    const [activeKeys, setActiveKeys] = useState<string[]>(persisted.current.keys);
    const [values, setValues] = useState<Record<string, string>>(persisted.current.vals);
    const [showAddMenu, setShowAddMenu] = useState(false);
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);
    const addBtnRef = useRef<HTMLDivElement>(null);
    const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});
    // Persist to localStorage on changes
    useEffect(() => {
        if (!storageKey) return;
        localStorage.setItem(storageKey, JSON.stringify({ activeKeys, values }));
    }, [activeKeys, values, storageKey]);

    // Close menus on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (showAddMenu && addBtnRef.current && !addBtnRef.current.contains(e.target as Node)) {
                setShowAddMenu(false);
            }
            if (openDropdown) {
                const ref = dropdownRefs.current[openDropdown];
                if (ref && !ref.contains(e.target as Node)) {
                    setOpenDropdown(null);
                }
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showAddMenu, openDropdown]);

    const triggerSearch = (updatedKeys: string[], updatedValues: Record<string, string>) => {
        const active: AppliedFilter[] = [];
        for (const k of updatedKeys) {
            const val = updatedValues[k];
            if (!val || !val.trim()) continue;
            active.push({ key: k, value: val });
        }
        onSearch(active);
    };

    const addFilter = (key: string) => {
        const next = [...activeKeys, key];
        setActiveKeys(next);
        setShowAddMenu(false);
        const field = schema.find(s => s.key === key);
        if (field?.type === 'enum') {
            setOpenDropdown(key);
        }
    };

    const removeFilter = (key: string) => {
        const nextKeys = activeKeys.filter(k => k !== key);
        const nextValues = { ...values };
        delete nextValues[key];
        setActiveKeys(nextKeys);
        setValues(nextValues);
        setOpenDropdown(null);
        triggerSearch(nextKeys, nextValues);
    };

    const selectEnumValue = (key: string, optionValue: string) => {
        const current = values[key];
        const next = current === optionValue ? '' : optionValue;
        const nextValues = { ...values, [key]: next };
        setValues(nextValues);
        triggerSearch(activeKeys, nextValues);
    };

    const updateTextValue = (key: string, value: string) => {
        setValues(prev => ({ ...prev, [key]: value }));
    };

    const submitTextFilter = () => {
        triggerSearch(activeKeys, values);
    };

    const clearAll = () => {
        setActiveKeys([]);
        setValues({});
        setOpenDropdown(null);
        onSearchChange?.('');
        onSearchSubmit?.('');
        onSearch([]);
        if (storageKey) localStorage.removeItem(storageKey);
    };

    const hasAnyFilter = activeKeys.length > 0 || searchValue.trim() !== '';
    const availableFields = schema.filter(s => !activeKeys.includes(s.key));
    const disabled = loading;

    const getChipLabel = (key: string): string => {
        const field = schema.find(s => s.key === key);
        if (!field) return key;
        const val = values[key];
        if (!val || !val.trim()) return field.label;
        if (field.type === 'enum' && field.options) {
            const opt = field.options.find(o => o.value === val);
            return `${field.label}: ${opt?.label || val}`;
        }
        return `${field.label}: ${val}`;
    };

    const renderChipDropdown = (key: string) => {
        const field = schema.find(s => s.key === key);
        if (!field || openDropdown !== key || field.type !== 'enum' || !field.options) return null;

        const selected = values[key] || '';
        return (
            <div className="absolute top-full left-0 mt-1 bg-white dark:bg-slate-800 border dark:border-slate-600 rounded-lg shadow-lg z-50 py-1 min-w-[180px] max-h-[200px] overflow-y-auto">
                {field.options.map(opt => (
                    <button
                        key={opt.value}
                        onClick={() => { selectEnumValue(key, opt.value); setOpenDropdown(null); }}
                        className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-slate-700 ${
                            selected === opt.value ? 'text-blue-700 dark:text-blue-300 font-medium' : 'text-gray-700 dark:text-gray-300'
                        }`}
                    >
                        <span className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center ${
                            selected === opt.value ? 'border-blue-500' : 'border-gray-300 dark:border-slate-500'
                        }`}>
                            {selected === opt.value && <span className="w-2 h-2 rounded-full bg-blue-500" />}
                        </span>
                        {opt.label}
                    </button>
                ))}
            </div>
        );
    };

    return (
        <div className={`flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 border-b-0 rounded-t-lg min-h-[42px] ${className || ''}`}>
            {leadingSlot && (
                <div className="flex items-center gap-2 border-r border-gray-200 dark:border-slate-700 pr-3 shrink-0">
                    {leadingSlot}
                </div>
            )}
            {/* Search input */}
            <div className="flex items-center gap-1.5 border-r border-gray-200 dark:border-slate-700 pr-3 shrink-0">
                <FiSearch size={14} className="text-gray-400" />
                <input
                    type="text"
                    className={`bg-transparent border-none outline-none text-sm text-gray-800 dark:text-white ${searchInputClassName} placeholder-gray-400`}
                    value={searchValue}
                    onChange={e => onSearchChange?.(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') onSearchSubmit?.(); }}
                    placeholder={searchPlaceholder}
                    disabled={disabled}
                />
            </div>

            {/* Active filter chips */}
            {activeKeys.map(key => {
                const field = schema.find(s => s.key === key);
                if (!field) return null;

                if (field.type === 'text') {
                    return (
                        <div key={key} className="flex items-center gap-1 bg-gray-100 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded px-2 py-1 text-xs shrink-0">
                            <span className="text-gray-500 dark:text-gray-400 font-medium">{field.label}:</span>
                            <input
                                type="text"
                                className="bg-transparent border-none outline-none text-xs text-gray-800 dark:text-white w-20"
                                value={values[key] || ''}
                                onChange={e => updateTextValue(key, e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') submitTextFilter(); }}
                                placeholder="..."
                                disabled={disabled}
                                autoFocus
                            />
                            <button onClick={() => removeFilter(key)} className="text-gray-400 hover:text-red-500" disabled={disabled}>
                                <FaTimes size={8} />
                            </button>
                        </div>
                    );
                }

                if (field.type === 'date') {
                    return (
                        <div key={key} className="flex items-center gap-1 bg-gray-100 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded px-2 py-1 text-xs shrink-0">
                            <span className="text-gray-500 dark:text-gray-400 font-medium">{field.label}:</span>
                            <input
                                type="date"
                                className="bg-transparent border-none outline-none text-xs text-gray-800 dark:text-white"
                                value={values[key] || ''}
                                onChange={e => { const nv = { ...values, [key]: e.target.value }; setValues(nv); triggerSearch(activeKeys, nv); }}
                                disabled={disabled}
                            />
                            <button onClick={() => removeFilter(key)} className="text-gray-400 hover:text-red-500" disabled={disabled}>
                                <FaTimes size={8} />
                            </button>
                        </div>
                    );
                }

                return (
                    <div key={key} ref={el => { dropdownRefs.current[key] = el; }} className="relative shrink-0">
                        <button
                            onClick={() => setOpenDropdown(openDropdown === key ? null : key)}
                            disabled={disabled}
                            className={`flex items-center gap-1 rounded px-2 py-1 text-xs border transition-colors ${
                                values[key]
                                    ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/50 dark:border-blue-700 dark:text-blue-300'
                                    : 'bg-gray-100 border-gray-200 text-gray-700 dark:bg-slate-700 dark:border-slate-600 dark:text-gray-300'
                            }`}
                        >
                            <span className="max-w-[200px] truncate">{getChipLabel(key)}</span>
                            <FaChevronDown size={8} className="text-gray-400" />
                            <span
                                onClick={(e) => { e.stopPropagation(); removeFilter(key); }}
                                className="ml-0.5 text-gray-400 hover:text-red-500 cursor-pointer"
                            >
                                <FaTimes size={8} />
                            </span>
                        </button>
                        {renderChipDropdown(key)}
                    </div>
                );
            })}

            {/* Active check item chips */}
            {checkItems && checkItems.filter(ci => ci.checked).map(ci => (
                <div key={ci.key} className="flex items-center gap-1 bg-orange-50 border border-orange-200 text-orange-700 dark:bg-orange-900/30 dark:border-orange-700 dark:text-orange-300 rounded px-2 py-1 text-xs shrink-0">
                    <span className="max-w-[200px] truncate">{ci.label}</span>
                    <button
                        type="button"
                        onClick={() => ci.onChange(false)}
                        aria-label={`Remover filtro: ${ci.label}`}
                        className="ml-0.5 text-orange-400 hover:text-red-500 cursor-pointer"
                    >
                        <FaTimes size={8} />
                    </button>
                </div>
            ))}

            {/* Add filter button */}
            {(availableFields.length > 0 || (checkItems && checkItems.length > 0)) && (
                <div ref={addBtnRef} className="relative shrink-0">
                    <button
                        onClick={() => setShowAddMenu(!showAddMenu)}
                        disabled={disabled}
                        className="flex items-center gap-1.5 px-2 py-1 text-xs text-gray-500 hover:text-blue-600 hover:bg-gray-100 dark:hover:bg-slate-700 rounded border border-dashed border-gray-300 dark:border-slate-600 transition-colors"
                        title="Adicionar filtro"
                    >
                        <FiFilter size={12} />
                        <span>Filtro</span>
                    </button>
                    {showAddMenu && (
                        <div className="absolute top-full left-0 mt-1 bg-white dark:bg-slate-800 border dark:border-slate-600 rounded-lg shadow-lg z-50 py-1 min-w-[180px]">
                            {availableFields.map(field => (
                                <button
                                    key={field.key}
                                    onClick={() => addFilter(field.key)}
                                    className="block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-300"
                                >
                                    {field.label}
                                </button>
                            ))}
                            {checkItems && checkItems.length > 0 && (
                                <>
                                    {availableFields.length > 0 && checkItems.some(ci => !ci.checked) && <hr className="my-1 border-gray-200 dark:border-slate-600" />}
                                    {checkItems.filter(ci => !ci.checked).map(item => (
                                        <label key={item.key} className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-300 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={item.checked}
                                                onChange={e => item.onChange(e.target.checked)}
                                                className="w-3.5 h-3.5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                            {item.label}
                                        </label>
                                    ))}
                                </>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Clear all filters */}
            {hasAnyFilter && (
                <button
                    onClick={clearAll}
                    disabled={disabled}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-red-500 shrink-0 ml-auto border border-gray-200 dark:border-slate-600 rounded transition-colors"
                    title="Limpar todos os filtros"
                >
                    <FaTimes size={9} />
                    <span>Limpar</span>
                </button>
            )}
        </div>
    );
};

export default FilterBar;
